'use client';

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  requestAnnouncements,
  type AnnouncementPayload,
} from './request-announcements';

const localStorageReadKey = 'read_announcements';

type AnnouncementsContextValue = {
  announcements: AnnouncementPayload[];
  readAnnouncementIds: string[];
  readIdSet: Set<string>;
  totalUnread: number;
  highPriorityAnnouncements: AnnouncementPayload[];
  mediumPriorityAnnouncements: AnnouncementPayload[];
  lowPriorityAnnouncements: AnnouncementPayload[];
  showHighPriorityToolbar: boolean;
  showMediumPriorityToolbar: boolean;
  showLowPriorityToolbar: boolean;
  refresh: () => Promise<void>;
  markRead: (ids: string[]) => Promise<void>;
};

const AnnouncementsContext =
  createContext<AnnouncementsContextValue | null>(null);

function uniqueStringIds(values: string[]): string[] {
  const seen: Record<string, true> = {};
  const output: string[] = [];
  values.forEach((value) => {
    if (!seen[value]) {
      seen[value] = true;
      output.push(value);
    }
  });
  return output;
}

function removeStaleReadIds(
  readIds: string[],
  activeAnnouncements: AnnouncementPayload[]
) {
  const activeIds = new Set(activeAnnouncements.map((announcement) => announcement.uuid).filter(Boolean));
  return readIds.filter((id) => activeIds.has(id));
}

const ANNOUNCEMENT_PRIORITY = {
  High: 'High',
  Medium: 'Medium',
  Low: 'Low',
} as const;

type AnnouncementPriority =
  (typeof ANNOUNCEMENT_PRIORITY)[keyof typeof ANNOUNCEMENT_PRIORITY];

function getPayloadPriority(
  announcement: AnnouncementPayload
): AnnouncementPriority {
  const rawPriority = (
    announcement as AnnouncementPayload & { priority?: string }
  ).priority;
  const normalizedPriority = rawPriority?.trim().toLowerCase();
  const normalizedType = announcement.type?.trim().toLowerCase();

  if (
    normalizedPriority === 'high' ||
    normalizedPriority === 'urgent' ||
    normalizedType === 'urgent'
  ) {
    return ANNOUNCEMENT_PRIORITY.High;
  }
  if (
    normalizedPriority === 'low' ||
    normalizedPriority === 'info' ||
    normalizedType === 'info'
  ) {
    return ANNOUNCEMENT_PRIORITY.Low;
  }

  return ANNOUNCEMENT_PRIORITY.Medium;
}

function getAnnouncementPrioritySortOrder(announcement: AnnouncementPayload): number {
  const priority = getPayloadPriority(announcement);
  if (priority === ANNOUNCEMENT_PRIORITY.High) {
    return 0;
  }
  if (priority === ANNOUNCEMENT_PRIORITY.Medium) {
    return 1;
  }
  return 2;
}

function loadReadIdsFromStorage(): string[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(localStorageReadKey);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return uniqueStringIds(parsed.filter((value) => typeof value === 'string'));
  } catch (error) {
    console.error('Failed reading notification IDs from localStorage.', error);
    return [];
  }
}

function saveReadIdsToStorage(readIds: string[]) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(localStorageReadKey, JSON.stringify(readIds));
  } catch (error) {
    console.error('Failed writing notification IDs to localStorage.', error);
  }
}

export function AnnouncementsProvider({ children }: PropsWithChildren) {
  const [announcements, setAnnouncements] = useState<AnnouncementPayload[]>([]);
  const [readAnnouncementIds, setReadAnnouncementIds] = useState<string[]>([]);

  const readIdSet = useMemo(() => new Set(readAnnouncementIds), [readAnnouncementIds]);

  const refresh = useCallback(async () => {
    const readIds = loadReadIdsFromStorage();

    let announcementState: AnnouncementPayload[] = [];
    try {
      const requestData = await requestAnnouncements();
      announcementState = requestData.sort(
        (a, b) =>
          getAnnouncementPrioritySortOrder(a) -
          getAnnouncementPrioritySortOrder(b)
      );
    } catch (error) {
      console.error('Failed requesting announcements.', error);
    }

    const nextReadIds = removeStaleReadIds(readIds, announcementState);
    saveReadIdsToStorage(nextReadIds);

    setAnnouncements(announcementState);
    setReadAnnouncementIds(nextReadIds);
  }, []);

  const markRead = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) {
        return;
      }
      const updatedIds = uniqueStringIds(readAnnouncementIds.concat(ids));
      setReadAnnouncementIds(updatedIds);
      saveReadIdsToStorage(updatedIds);
    },
    [readAnnouncementIds]
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const highPriorityAnnouncements = useMemo(
    () =>
      announcements.filter(
        (announcement) =>
          getPayloadPriority(announcement) === ANNOUNCEMENT_PRIORITY.High
      ),
    [announcements]
  );

  const mediumPriorityAnnouncements = useMemo(
    () =>
      announcements.filter(
        (announcement) =>
          getPayloadPriority(announcement) === ANNOUNCEMENT_PRIORITY.Medium &&
          !!announcement.uuid &&
          !readIdSet.has(announcement.uuid)
      ),
    [announcements, readIdSet]
  );

  const lowPriorityAnnouncements = useMemo(
    () =>
      announcements.filter(
        (announcement) =>
          getPayloadPriority(announcement) === ANNOUNCEMENT_PRIORITY.Low &&
          !!announcement.uuid &&
          !readIdSet.has(announcement.uuid)
      ),
    [announcements, readIdSet]
  );

  const value = useMemo<AnnouncementsContextValue>(
    () => ({
      announcements,
      readAnnouncementIds,
      readIdSet,
      totalUnread: Math.max(0, announcements.length - readIdSet.size),
      highPriorityAnnouncements,
      mediumPriorityAnnouncements,
      lowPriorityAnnouncements,
      showHighPriorityToolbar: highPriorityAnnouncements.length > 0,
      showMediumPriorityToolbar: mediumPriorityAnnouncements.length > 0,
      showLowPriorityToolbar: lowPriorityAnnouncements.length > 0,
      refresh,
      markRead,
    }),
    [
      announcements,
      readAnnouncementIds,
      readIdSet,
      highPriorityAnnouncements,
      mediumPriorityAnnouncements,
      lowPriorityAnnouncements,
      refresh,
      markRead,
    ]
  );

  return (
    <AnnouncementsContext.Provider value={value}>
      {children}
    </AnnouncementsContext.Provider>
  );
}

export function useAnnouncements() {
  const context = useContext(AnnouncementsContext);
  if (!context) {
    throw new Error(
      'useAnnouncements must be used inside AnnouncementsProvider.'
    );
  }
  return context;
}
